import React from "react";
import { InlineMath } from "react-katex";

import PolylineGraphInput from "../../../../components/GraphPlotter/tasks/PolylineGraphInput";

const X_RANGE = [-6, 6];
const SHIFT_X_RANGE = [-8, 8];
const Y_RANGE = [-6, 6];
const SHIFT_Y_RANGE = [-8, 8];
const EDGE_MARGIN = 1;

const baseShapes = [
  [
    { x: -5, y: -2, closed: false },
    { x: -2, y: 1, closed: true },
    { x: 1, y: -1, closed: true },
    { x: 4, y: 0, closed: true },
  ],
  [
    { x: -4, y: -1, closed: true },
    { x: -2, y: -3, closed: true },
    { x: 1, y: 2, closed: true },
    { x: 4, y: 1, closed: false },
  ],
  [
    { x: -4, y: 2, closed: true },
    { x: -1, y: 0, closed: true },
    { x: 2, y: 3, closed: true },
    { x: 5, y: -1, closed: false },
  ],
  [
    { x: -5, y: 1, closed: false },
    { x: -3, y: -2, closed: true },
    { x: 0, y: 2, closed: true },
    { x: 2, y: -1, closed: true },
    { x: 5, y: 1, closed: true },
  ],
  [
    { x: -5, y: -1, closed: true },
    { x: -2, y: -1, closed: true },
    { x: 1, y: 2, closed: true },
    { x: 4, y: 0, closed: false },
  ],
  [
    { x: -4, y: 2, closed: false },
    { x: -1, y: 2, closed: true },
    { x: 2, y: -1, closed: true },
    { x: 5, y: -1, closed: true },
  ],
  [
    { x: -5, y: 0, closed: true },
    { x: -2, y: 3, closed: true },
    { x: 1, y: 3, closed: true },
    { x: 4, y: -2, closed: false },
  ],
  [
    { x: -5, y: -3, closed: true },
    { x: -3, y: 0, closed: true },
    { x: 0, y: 0, closed: true },
    { x: 3, y: 2, closed: true },
  ],
  [
    { x: -4, y: 1, closed: false },
    { x: -1, y: -2, closed: true },
    { x: 2, y: 2, closed: true },
    { x: 5, y: 2, closed: true },
  ],
  [
    { x: -5, y: 2, closed: true },
    { x: -2, y: -1, closed: true },
    { x: 1, y: -1, closed: true },
    { x: 4, y: 3, closed: false },
  ],
  [
    { x: -4, y: -2, closed: false },
    { x: -2, y: 2, closed: true },
    { x: 0, y: -1, closed: true },
    { x: 3, y: -1, closed: true },
    { x: 5, y: 1, closed: true },
  ],
  [
    { x: -5, y: 3, closed: true },
    { x: -3, y: 1, closed: true },
    { x: -1, y: 1, closed: true },
    { x: 2, y: -2, closed: true },
    { x: 5, y: 0, closed: false },
  ],
];

const xShiftVectors = [-3, -2, -1, 1, 2, 3].map((p) => ({ p, q: 0 }));
const yShiftVectors = [-3, -2, -1, 1, 2, 3].map((q) => ({ p: 0, q }));
const xyShiftVectors = [
  { p: 1, q: 1 },
  { p: 1, q: -1 },
  { p: -1, q: 1 },
  { p: -1, q: -1 },
  { p: 2, q: 1 },
  { p: 2, q: -1 },
  { p: -2, q: 1 },
  { p: -2, q: -1 },
  { p: 1, q: 2 },
  { p: -1, q: 2 },
  { p: 1, q: -2 },
  { p: -1, q: -2 },
  { p: 3, q: 1 },
  { p: -3, q: 1 },
  { p: 3, q: -1 },
  { p: -3, q: -1 },
  { p: 1, q: 3 },
  { p: -1, q: 3 },
  { p: 1, q: -3 },
  { p: -1, q: -3 },
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function normalizePoint(point, index, length) {
  return {
    x: Number(point.x),
    y: Number(point.y),
    closed: index === 0 || index === length - 1 ? point.closed !== false : true,
  };
}

function samePoints(userPoints = [], expectedPoints = []) {
  if (!Array.isArray(userPoints) || userPoints.length !== expectedPoints.length) return false;

  return expectedPoints.every((expected, index) => {
    const got = normalizePoint(userPoints[index], index, userPoints.length);
    const want = normalizePoint(expected, index, expectedPoints.length);
    return got.x === want.x && got.y === want.y && got.closed === want.closed;
  });
}

function inRange(points, xRange = X_RANGE, yRange = Y_RANGE, margin = 0) {
  return points.every(
    (point) =>
      point.x >= xRange[0] + margin &&
      point.x <= xRange[1] - margin &&
      point.y >= yRange[0] + margin &&
      point.y <= yRange[1] - margin
  );
}

function transformPoint(point, mode, params) {
  if (mode === "shift") {
    return { ...point, x: point.x + params.p, y: point.y + params.q };
  }
  if (params.symmetry === "xAxis") return { ...point, y: -point.y };
  if (params.symmetry === "yAxis") return { ...point, x: -point.x };
  return { ...point, x: -point.x, y: -point.y };
}

function transformPolyline(points, mode, params) {
  return points.map((point) => transformPoint(point, mode, params)).sort((a, b) => a.x - b.x);
}

function pointKey(point) {
  return point.x + ":" + point.y;
}

function endPointKeys(points) {
  if (!Array.isArray(points) || points.length === 0) return new Set();
  return new Set([pointKey(points[0]), pointKey(points[points.length - 1])]);
}

function hasOverlappingEndpoints(base, expected) {
  const baseEnds = endPointKeys(base);
  return endPointKeys(expected).size > 0 && [...endPointKeys(expected)].some((key) => baseEnds.has(key));
}

function makeShiftTask(option) {
  const vectors = option === "x" ? xShiftVectors : option === "y" ? yShiftVectors : xyShiftVectors;

  for (let attempt = 0; attempt < 50; attempt += 1) {
    const base = pick(baseShapes);
    const vector = pick(vectors);
    const expected = transformPolyline(base, "shift", vector);
    const opposite = transformPolyline(base, "shift", { p: -vector.p, q: -vector.q });

    if (
      inRange(base, SHIFT_X_RANGE, SHIFT_Y_RANGE, EDGE_MARGIN) &&
      inRange(expected, SHIFT_X_RANGE, SHIFT_Y_RANGE, EDGE_MARGIN) &&
      inRange(opposite, SHIFT_X_RANGE, SHIFT_Y_RANGE, EDGE_MARGIN) &&
      !hasOverlappingEndpoints(base, expected)
    ) {
      return { base, expected, ...vector };
    }
  }

  const base = baseShapes[0];
  const expected = transformPolyline(base, "shift", { p: 1, q: 1 });
  return { base, expected, p: 1, q: 1 };
}

function makeSymmetryTask(symmetry) {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const base = pick(baseShapes);
    const expected = transformPolyline(base, "symmetry", { symmetry });
    if (!hasOverlappingEndpoints(base, expected)) return { base, expected, symmetry };
  }

  const base = baseShapes.find((shape) => {
    const expected = transformPolyline(shape, "symmetry", { symmetry });
    return !hasOverlappingEndpoints(shape, expected);
  }) || baseShapes[0];
  const expected = transformPolyline(base, "symmetry", { symmetry });
  return { base, expected, symmetry };
}

function vectorLatex(p, q) {
  return `\\left[${p},\\ ${q}\\right]`;
}

function shiftInsideLatex(p) {
  if (p === 0) return "x";
  return p > 0 ? `x-${p}` : `x+${Math.abs(p)}`;
}

function shiftOutsideLatex(q) {
  if (q === 0) return "";
  return q > 0 ? `+${q}` : `-${Math.abs(q)}`;
}

function shiftFormulaLatex(p, q) {
  return `g(x)=f(${shiftInsideLatex(p)})${shiftOutsideLatex(q)}`;
}

function symmetryLabel(symmetry) {
  if (symmetry === "xAxis") return <>osi <InlineMath math={"OX"} /></>;
  if (symmetry === "yAxis") return <>osi <InlineMath math={"OY"} /></>;
  return <>środka układu współrzędnych</>;
}

export function generateTask(mode = "shift", option) {
  if (mode === "symmetry") {
    const symmetry = option || pick(["xAxis", "yAxis", "origin"]);
    const data = makeSymmetryTask(symmetry);
    return {
      type: "PolylineTransformSketchTask",
      data: { mode: "symmetry", ...data, xRange: X_RANGE, yRange: Y_RANGE },
    };
  }

  const data = makeShiftTask(option);
  return {
    type: "PolylineTransformSketchTask",
    data: { mode: "shift", ...data, xRange: SHIFT_X_RANGE, yRange: SHIFT_Y_RANGE },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6 }}>
      {data.mode === "shift" ? (
        <p>
          Na podstawie fioletowego wykresu funkcji <InlineMath math={"f"} /> dorysuj pomarańczowy wykres funkcji{" "}
          <InlineMath math={"g"} />, określonej wzorem <InlineMath math={shiftFormulaLatex(data.p, data.q)} />.
        </p>
      ) : (
        <p>
          Na podstawie fioletowego wykresu funkcji <InlineMath math={"f"} /> dorysuj pomarańczowy wykres funkcji{" "}
          <InlineMath math={"g"} />, otrzymany przez symetrię względem {symmetryLabel(data.symmetry)}.
        </p>
      )}
    </div>
  );
}

export function renderInput(task, userInput = {}, onChange, disabled) {
  return (
    <PolylineGraphInput
      value={userInput}
      onChange={onChange}
      disabled={disabled}
      xRange={task.data.xRange}
      yRange={task.data.yRange}
      referencePoints={task.data.base}
      referenceColor="#8b5cf6"
      answerColor="#ff9900"
    />
  );
}

function ExercisePolylineInput({ task }) {
  const [value, setValue] = React.useState({ points: [] });
  return renderInput(task, value, setValue, false);
}

export function renderExerciseTask(task) {
  return (
    <div>
      {renderTask(task)}
      <ExercisePolylineInput task={task} />
    </div>
  );
}

export function checkAnswer(task, userInput = {}) {
  return samePoints(userInput.points, task.data.expected);
}

export function renderAnswer({ data }) {
  return (
    <PolylineGraphInput
      value={{ points: data.expected }}
      disabled
      xRange={data.xRange}
      yRange={data.yRange}
      referencePoints={data.base}
      referenceColor="#8b5cf6"
      answerColor="#ff9900"
      showControls={false}
    />
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {data.mode === "shift" ? (
        <div>
          <p>
            Ze wzoru <InlineMath math={shiftFormulaLatex(data.p, data.q)} /> odczytujemy wektor przesunięcia{" "}
            <InlineMath math={vectorLatex(data.p, data.q)} />.
          </p>
          <p>
            W argumencie funkcji znak działa przeciwnie, więc <InlineMath math={`f(${shiftInsideLatex(data.p)})`} />
            przesuwa poziomo o <InlineMath math={String(data.p)} />. Dopisek{" "}
            <InlineMath math={shiftOutsideLatex(data.q) || "+0"} /> przesuwa pionowo o{" "}
            <InlineMath math={String(data.q)} />.
          </p>
        </div>
      ) : (
        <p>
          Każdy punkt wykresu odbijamy względem {symmetryLabel(data.symmetry)} i łączymy punkty w tej samej kolejności
          wzdłuż wykresu.
        </p>
      )}
      {renderAnswer({ data })}
    </div>
  );
}

export function renderUserAnswer(arg1 = {}, arg2 = {}) {
  const firstIsTask = arg1?.type === "PolylineTransformSketchTask";
  const task = firstIsTask ? arg1 : arg2?.type === "PolylineTransformSketchTask" ? arg2 : null;
  const userInput = firstIsTask ? arg2 : arg1;
  const points = Array.isArray(userInput?.points) ? userInput.points : [];
  if (!points.length) return <i>(brak odpowiedzi)</i>;
  if (!task?.data) return <span>{points.map((point) => `(${point.x}, ${point.y})`).join(" - ")}</span>;

  return (
    <div style={{ marginTop: 8 }}>
      <PolylineGraphInput
        value={{ points }}
        disabled
        xRange={task.data.xRange}
        yRange={task.data.yRange}
        referencePoints={task.data.base}
        referenceColor="#8b5cf6"
        answerColor="#ff9900"
        showControls={false}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  renderExerciseTask,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
